/* eslint-disable react/prop-types */
import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import URLSearchParams from 'url-search-params';
import {
  Redirect, Route, Switch, useHistory, useLocation, useRouteMatch,
} from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { IntlProvider } from 'react-intl';

import AppLocale from 'lngProvider';
import { setInitUrl } from 'redux/actions/Auth';
import { onLayoutTypeChange, onNavStyleChange, setThemeType } from 'redux/actions/Setting';
import HealthCheck from 'components/common/health';
import CircularProgress from 'components/common/CircularProgress';
import MainApp from './MainApp';
import SignIn from '../SignIn';
import SignInPuppeteer from '../../routes/Puppeteer/SignInPuppeteer';
import ForgotPassword from '../ForgotPassword';
import PasswordResetForm from '../PasswordResetForm';
import SignUp from '../SignUp';

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
} from '../../constants/ThemeSetting';
import { Environments } from '../../constants/System';
import { setTimeZone } from '../../redux/actions/Setting';
import PCSR from '../../routes/Puppeteer/PuppeteerCreateSettlementReport';
import PVSR from '../../routes/Puppeteer/PuppeteerViewSettlementReport';

const RestrictedRoute = ({
  component: Component, location, token, ...rest
}) => (
  <Route
    {...rest}
    render={(props) => (token
      ? <Component {...props} />
      : (
        <Redirect
          to={{
            pathname: '/signin',
            state: { from: location },
          }}
        />
      ))}
  />
);

const App = () => {
  const dispatch = useDispatch();
  const { locale, navStyle, layoutType } = useSelector(({ settings }) => settings);
  const {
    token,
    initURL,
    loadingAuthUser,
    authUser,
  } = useSelector(({ auth }) => auth);

  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const getHref = () => {
    let href = '/css/blue.css';
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case Environments.SANDBOX:
        href = '/css/red.css';
        break;
      case Environments.DEVELOPMENT:
        href = '/css/deep_orange.css';
        break;
      default:
        href = '/css/blue.css';
    }

    return href;
  };

  useEffect(() => {
    // eslint-disable-next-line dot-notation
    if (token && authUser && authUser['_id']) {
      // dispatch(getUser(authUser._id)); TODO: Fix this when fetching user info
    }
  }, [dispatch, token, authUser]);

  useEffect(() => {
    const link = document.createElement('link');
    link.type = 'text/css';
    link.rel = 'stylesheet';
    link.href = getHref();
    link.className = 'gx-style';
    document.body.appendChild(link);
  });

  const setLayoutType = (layout) => {
    if (layout === LAYOUT_TYPE_FULL) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('full-layout');
    } else if (layout === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove('full-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('boxed-layout');
    } else if (layout === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('full-layout');
      document.body.classList.add('framed-layout');
    }
  };

  const setNavStyle = (style) => {
    if (style === NAV_STYLE_DEFAULT_HORIZONTAL
      || style === NAV_STYLE_DARK_HORIZONTAL
      || style === NAV_STYLE_INSIDE_HEADER_HORIZONTAL
      || style === NAV_STYLE_ABOVE_HEADER
      || style === NAV_STYLE_BELOW_HEADER) {
      document.body.classList.add('full-scroll');
      document.body.classList.add('horizontal-layout');
    } else {
      document.body.classList.remove('full-scroll');
      document.body.classList.remove('horizontal-layout');
    }
  };

  useEffect(() => {
    if (initURL === '') {
      dispatch(setInitUrl(location.pathname));
    }
    const params = new URLSearchParams(location.search);

    if (params.has('theme')) {
      dispatch(setThemeType(params.get('theme')));
    }
    if (params.has('nav-style')) {
      dispatch(onNavStyleChange(params.get('nav-style')));
    }
    if (params.has('layout-type')) {
      dispatch(onLayoutTypeChange(params.get('layout-type')));
    }
    setLayoutType(layoutType);
    setNavStyle(navStyle);

    const defaultTimeZone = { value: 'Etc/UTC', label: 'UTC', offset: 0 };
    try {
      dispatch(setTimeZone(JSON.parse(sessionStorage.getItem('timezone')) || defaultTimeZone));
    } catch (error) {
      dispatch(setTimeZone(defaultTimeZone));
    }
  }, [dispatch, initURL, layoutType, location.pathname, location.search, navStyle]);

  const currentAppLocale = AppLocale[locale.locale];

  return loadingAuthUser ? <CircularProgress /> : (
    <ConfigProvider locale={currentAppLocale.antd}>
      <IntlProvider
        locale={currentAppLocale.locale}
        messages={currentAppLocale.messages}
      >

        <Switch>
          <Route exact path="/health-check" component={HealthCheck} />
          <Route exact path="/puppeteer-signin" component={SignInPuppeteer} />
          <Route exact path="/puppeteer-create-reporter" component={PCSR} />
          <Route exact path="/puppeteer-view-reporter" component={PVSR} />
          <Route exact path="/signin" component={SignIn} />
          <Route exact path="/signup" component={SignUp} />
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route exact path="/password-reset" component={PasswordResetForm} />
          <RestrictedRoute
            path={`${match.url}`}
            token={token}
            location={location}
            component={MainApp}
          />
        </Switch>
      </IntlProvider>
    </ConfigProvider>
  );
};

export default memo(App);
